import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { useProjects } from '@/hooks/useProjects.js';
import { useProjectIssueTypes, usePriorities, useAssignableUsers } from '@/hooks/useMeta.js';
import { AssigneeOptions } from '@/components/AssigneeOptions.js';
import { IssueKeysInput } from '@/components/IssueKeysInput.js';
import type { JiraIssuePickerResult } from '@shared/types.js';
import type { RichEditorHandle } from '@/features/editor/RichEditor.js';
import { LazyRichEditor as RichEditor } from '@/features/editor/LazyRichEditor.js';
import { editorToAdf, resolvePendingAttachments } from '@/features/editor/adf-convert.js';

/**
 * Files added to the description before the issue exists are uploaded once it
 * does, then the description is updated to embed them. Upload failures leave
 * the issue created without those files.
 */
async function attachPendingFiles(issueKey: string, editor: RichEditorHandle): Promise<void> {
  const pending = editor.getPendingFiles();
  if (pending.size === 0) return;
  const uploaded = new Map<string, string>();
  for (const [pendingKey, file] of pending) {
    try {
      const form = new FormData();
      form.append('files', file);
      const res = await api.postForm<Array<{ mediaApiFileId: string | null }>>(`/api/issues/${issueKey}/attachments`, form);
      const mediaId = res.data[0]?.mediaApiFileId;
      if (mediaId) uploaded.set(pendingKey, mediaId);
    } catch {
      // Keep going; the issue already exists
    }
  }
  if (uploaded.size === 0) return;
  const doc = editorToAdf(resolvePendingAttachments(editor.getEditorJson(), uploaded));
  await api.put(`/api/issues/${issueKey}`, { descriptionAdf: doc }).catch(() => undefined);
}

interface CreateIssueDialogProps {
  open: boolean;
  onClose: () => void;
  defaultProject?: string;
}

export function CreateIssueDialog({ open, onClose, defaultProject }: CreateIssueDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [projectKey, setProjectKey] = useState(defaultProject || '');
  const [issueType, setIssueType] = useState('Task');
  const [summary, setSummary] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const descriptionRef = useRef<RichEditorHandle>(null);
  const [priority, setPriority] = useState('Medium');
  const [assignee, setAssignee] = useState('');
  const [blockedBy, setBlockedBy] = useState<JiraIssuePickerResult[]>([]);

  const { data: projects } = useProjects();
  const { data: issueTypes } = useProjectIssueTypes(projectKey);
  const { data: priorities } = usePriorities();
  const { data: users, isLoading: usersLoading } = useAssignableUsers(projectKey);

  // Fall back to the project's first type / first priority when the current
  // choice doesn't exist there (e.g. a project with no "Task" type), so we never
  // submit a value the select isn't actually showing.
  const effectiveIssueType = issueTypes?.length && !issueTypes.some((t) => t.name === issueType)
    ? issueTypes[0]!.name
    : issueType;
  const effectivePriority = priorities?.length && !priorities.some((p) => p.name === priority)
    ? priorities[0]!.name
    : priority;

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const created = await api.post<{ key: string; reporterWarning?: string }>('/api/issues', data).then((r) => r.data);
      if (descriptionRef.current) await attachPendingFiles(created.key, descriptionRef.current);
      return created;
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['board'] });
      resetForm();
      onClose();
      navigate(`/issues/${result.key}`, { state: { reporterWarning: result.reporterWarning } });
    },
  });

  const resetForm = useCallback(() => {
    setProjectKey(defaultProject || '');
    setIssueType('Task');
    setSummary('');
    setEditorKey((k) => k + 1);
    setPriority('Medium');
    setAssignee('');
    setBlockedBy([]);
  }, [defaultProject]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!projectKey || !summary.trim()) return;

    createMutation.mutate({
      project: projectKey,
      issuetype: effectiveIssueType,
      summary: summary.trim(),
      descriptionAdf: descriptionRef.current && !descriptionRef.current.isEmpty() ? descriptionRef.current.getAdf() : undefined,
      priority: effectivePriority,
      assignee: assignee || undefined,
      blockedBy: blockedBy.length ? blockedBy.map((b) => b.key) : undefined,
    });
  }, [projectKey, effectiveIssueType, summary, effectivePriority, assignee, blockedBy, createMutation]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Create Issue</h2>
          <button onClick={handleClose} className="text-2xl text-gray-400 hover:text-gray-600">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Project */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Project *</label>
            <select
              value={projectKey}
              onChange={(e) => { setProjectKey(e.target.value); setAssignee(''); }}
              required
              className="w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="">Select project...</option>
              {projects?.map((p) => (
                <option key={p.key} value={p.key}>{p.name} ({p.key})</option>
              ))}
            </select>
          </div>

          {/* Issue Type */}
          {projectKey && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
              <select
                value={effectiveIssueType}
                onChange={(e) => setIssueType(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              >
                {(issueTypes || [{ id: '', name: 'Task' }]).map((t) => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Summary */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Summary *</label>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              required
              maxLength={255}
              placeholder="Brief description of the issue"
              className="w-full rounded-md border px-3 py-2 text-sm"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
            <RichEditor
              key={editorKey}
              ref={descriptionRef}
              projectKey={projectKey || undefined}
              placeholder="Detailed description (optional) — paste or drop screenshots"
              minHeight={120}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Priority */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
              <select
                value={effectivePriority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
              >
                {(priorities || [{ id: '', name: 'Medium' }]).map((p) => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Assignee */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Assignee {usersLoading && projectKey ? '(loading...)' : ''}
              </label>
              <select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                disabled={!projectKey}
              >
                <AssigneeOptions users={users} />
              </select>
              {!projectKey && (
                <p className="mt-1 text-xs text-gray-400">Select a project first</p>
              )}
            </div>
          </div>

          {/* Blocked by */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Blocked by</label>
            <IssueKeysInput
              value={blockedBy}
              onChange={setBlockedBy}
              label="Blocked by"
              placeholder="Work that must be done first (optional) — search by key or summary"
            />
          </div>

          {createMutation.isError && (
            <p className="text-sm text-red-600">Failed to create issue. Please try again.</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!projectKey || !summary.trim() || createMutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
